/**
 * classifyHand — ADR-024 §2.4: name the hand's region of the curl × adduction
 * plane, from measurements the placement already made.
 *
 * ONE responsibility: given per-digit placement outcomes, say which
 * prototypical shape this hand is — `flat`, `relaxed`, or `cupped` — so the
 * graft report can carry it and the surface can decide when to PROPOSE the
 * knuckle row (D3: propose, gizmo, let them self-approve — never a silent
 * quality drop on exactly the hands that need help). Data in, a name out: no
 * grid, no policy, no UI.
 *
 * THE TWO AXES ARE INDEPENDENT MEASUREMENTS THAT PREDICT DIFFERENT FAILURES
 * (ADR-023 §12.7):
 *
 *  - **curl** — whether the biometric CHORD leaves the flesh (the §1.2
 *    problem). Operationalized as the ESCAPE SWITCH'S OWN VERDICT per digit,
 *    not as a length ratio: the corpus run that amended D1 (ADR-024 §11.1)
 *    showed `path/chord` cannot separate lattice meander from a real curl,
 *    while chord-escape separates perfectly. A digit that took the medial
 *    path IS a digit whose chord was measured wrong.
 *  - **adduction** — whether adjacent digits TOUCH (the §12.1 problem: one
 *    connected component, so routing can leak between neighbours). Measured
 *    as mid-digit stations of adjacent fingers sitting within their summed
 *    radii (plus a voxel of quantization).
 *
 * The prototypes are regions of that plane, and the CONJUNCTION is the worst
 * case — not "more curled": a curled hand with splayed fingers routes cleanly
 * (nothing to leak into), and a barely-curled adducted hand keeps its chords
 * (routing is never asked). §12.7 verbatim.
 *
 * THE CLASS IS AN OUTPUT OF MEASUREMENT, NEVER AN INPUT A CALLER MAY ASSERT
 * (the family-detection-from-signature lesson: metadata lies, measurement
 * does not). And it stays EVIDENCE rather than a gate: a hand that measures
 * cupped still grafts — it grafts with more help offered.
 *
 * Depends on: propose-digit-names (DigitName, DIGIT_ORDER), voxel-grid types.
 * Depended on by: place-digits (mints the report), the graft report, the app's
 * propose-the-second-row decision.
 */
import { DIGIT_ORDER, type DigitName } from './propose-digit-names.js';
import type { VoxelPoint } from './voxel-grid.js';

/** One digit's classification inputs — all read off the placement's own work. */
export interface DigitClassSample {
  readonly name: DigitName;
  /** Did the escape switch send this digit to the medial path? */
  readonly escaped: boolean;
  /**
   * A mid-digit station and the tube's half-width there, or null when the
   * tube could not be walked (an unwitnessed digit is not evidence of
   * anything, including separation).
   */
  readonly mid: VoxelPoint | null;
  readonly midRadius: number | null;
}

export type HandClass = 'flat' | 'relaxed' | 'cupped';

export interface HandClassReport {
  readonly klass: HandClass;
  /** How many digits' chords the volume measured wrong — the curl axis. */
  readonly escapedDigits: number;
  /** Adjacent finger pairs whose tubes touch at mid-digit — the adduction axis. */
  readonly adductedPairs: readonly (readonly [DigitName, DigitName])[];
}

/** Quantization slack on the touch test — a voxel, the grid's own resolution. */
const TOUCH_SLACK_VOXELS = 1;

export function classifyHand(samples: readonly DigitClassSample[]): HandClassReport {
  const escaped = samples.filter((s) => s.escaped).length;

  // Adduction: FINGERS in anatomical order, each against its row neighbour.
  // The thumb is excluded on purpose — it opposes the fingers by design, and
  // its proximity to the index is anatomy, not a routing hazard.
  const fingers = samples
    .filter((s) => s.name !== 'Thumb')
    .sort((a, b) => DIGIT_ORDER.indexOf(a.name) - DIGIT_ORDER.indexOf(b.name));
  const adducted: (readonly [DigitName, DigitName])[] = [];
  for (let k = 1; k < fingers.length; k += 1) {
    const a = fingers[k - 1]!;
    const b = fingers[k]!;
    if (a.mid === null || b.mid === null || a.midRadius === null || b.midRadius === null) continue;
    const gap = Math.hypot(a.mid.x - b.mid.x, a.mid.y - b.mid.y, a.mid.z - b.mid.z);
    if (gap <= a.midRadius + b.midRadius + TOUCH_SLACK_VOXELS) adducted.push([a.name, b.name]);
  }

  const klass: HandClass = escaped === 0 ? 'flat' : adducted.length > 0 ? 'cupped' : 'relaxed';
  return { klass, escapedDigits: escaped, adductedPairs: adducted };
}
